import {mapChatMessagesToApiHistory} from '../../aiMessageGenerator/utils/types';
import {getAiAgentContext, sendAiAgentMessage} from '../../../redux/aiAgent/aiAgentActions';
import setGlobalState from '../../../redux/setGlobalState';
import store from '../../../store';
import {detectChatLangFromText, shouldLockChatLang} from './detectChatLang';
import {mergeCurrentFormValues} from './agentFormValues';
import {resetAgentEventState} from './resetAgentEventState';
import {resolveAttachmentImage} from './resolveAttachmentImage';
import {buildAgentTurnResult} from './resolveAgentTurn';
import {runAgentProposal} from './runAgentProposal';
import {
    isNewEventRequest,
    rememberEventTypeFromAsk,
    rememberProposalContext,
    rememberUiActionContext,
} from './agentSessionMemory';
import {
    clearHeldProposal,
    holdProposal,
    isProposalConfirmPrompt,
    takeHeldProposal,
} from './agentProposalHold';
import type {AiAgentProposalOptions, RunAgentProposalStatus} from './runAgentProposal';
import type {NavigateFn} from './navigateAfterExecute';
import type {AiAgentSubmitHandler} from '../types';
import type {AiAgentChatLang, AgentResponse, AiAgentProposalOwner} from '../../../redux/aiAgent/aiAgent.type';
import type {ChatAskPrompt, DraftResult} from '../../aiMessageGenerator/utils/types';

type AiAgentSubmitOptions = AiAgentProposalOptions & {
    imageTooLarge: string;
    imageUnsupported: string;
    proposalConfirmed: string;
    proposalAlreadyCreated: string;
    proposalOtherOwner: string;
};

/** The language of the first free-text turn is the one the chat keeps answering in. */
function resolveChatLang(prompt: string, pendingAsk: ChatAskPrompt | null): AiAgentChatLang | null {
    const chatLang: AiAgentChatLang | null = store.getState().aiAgent?.chatLang ?? null;
    if(chatLang || !shouldLockChatLang(prompt, pendingAsk)) {
        return chatLang;
    }
    const detected = detectChatLangFromText(prompt);
    setGlobalState('aiAgent.chatLang', detected);
    return detected;
}

function rememberTurnContext(payload: AgentResponse): void {
    const {uiAction, pendingProposal} = payload;
    if(isNewEventRequest(uiAction.payload) || pendingProposal?.newEvent) {
        resetAgentEventState();
    }
    if(pendingProposal) {
        rememberProposalContext(pendingProposal);
        mergeCurrentFormValues(pendingProposal.fields);
        return;
    }
    rememberUiActionContext(uiAction);
    mergeCurrentFormValues(uiAction.payload?.fields);
}

function confirmedText(status: RunAgentProposalStatus, options: AiAgentSubmitOptions): string {
    return status === 'repeat' ? options.proposalAlreadyCreated : options.proposalConfirmed;
}

/**
 * The producer answered the confirmation, so the proposal the summary turn held now runs.
 * Nothing is sent to the server first — the values were already agreed on in the chat.
 */
async function confirmHeldProposal(
    sessionId: string,
    navigate: NavigateFn,
    options: AiAgentSubmitOptions,
): Promise<DraftResult | null> {
    // Context first: taking the proposal also drops it, and there is nothing to run it
    // against here — the summary has to stay confirmable once the owner is known again.
    const {eid, pid, scope} = getAiAgentContext();
    if(!eid && !pid) {
        return null;
    }
    const owner: AiAgentProposalOwner = {scope, eid, pid};
    const held = takeHeldProposal(owner);
    if(held.status === 'none') {
        return null;
    }
    // The producer moved to another event since the summary — creating here would put the
    // record on the wrong one. The proposal stays held for the event it belongs to.
    if(held.status === 'otherOwner') {
        return {text: options.proposalOtherOwner};
    }
    const {proposal} = held;

    const result = await runAgentProposal({
        proposal,
        agentText: '',
        execute: {sessionId, scope, eid, pid},
        navigate,
        options,
    });
    if(result.status === 'failed') {
        // Nothing was created, so the summary stays confirmable — the producer presses again
        // instead of dictating every value a second time.
        holdProposal(proposal, owner);
    }
    // A failure brings its own wording. The rest ran without the agent saying anything this
    // turn, so the answer has to state what the press actually did.
    return {...result, text: result.text || confirmedText(result.status, options)};
}

export function createAiAgentSubmitHandler(
    navigate: NavigateFn,
    options: AiAgentSubmitOptions,
): AiAgentSubmitHandler {
    return async (prompt, history, sessionId, context) => {
        const trimmedPrompt = prompt.trim();
        if(isProposalConfirmPrompt(trimmedPrompt)) {
            return confirmHeldProposal(sessionId, navigate, options);
        }
        // Anything else answers the summary with a change, so the held proposal is stale —
        // the server re-proposes with the change, and that turn holds the new one.
        clearHeldProposal();
        rememberEventTypeFromAsk(trimmedPrompt, context.pendingAsk);

        const {eid, pid, scope, currentRoute, formId, eventType, sessionIntent, currentFormValues} = getAiAgentContext();
        if(!eid && !pid) {
            return null;
        }

        const {dataUrl: imageDataUrl, error: imageError} = await resolveAttachmentImage(context.attachment);
        if(imageError) {
            return {text: imageError === 'tooLarge' ? options.imageTooLarge : options.imageUnsupported};
        }
        if(!trimmedPrompt && !imageDataUrl) {
            return null;
        }

        const agentPayload = await sendAiAgentMessage({
            prompt: trimmedPrompt,
            history: mapChatMessagesToApiHistory(history),
            sessionId,
            scope,
            currentRoute,
            eid,
            pid,
            formId,
            eventType,
            sessionIntent,
            chatLang: resolveChatLang(trimmedPrompt, context.pendingAsk),
            currentFormValues,
            imageDataUrl,
            pendingAsk: context.pendingAsk
                ? {question: context.pendingAsk.question, options: context.pendingAsk.options}
                : null,
        });

        if(!agentPayload) {
            return null;
        }

        rememberEventTypeFromAsk(agentPayload.answeredOption?.value, context.pendingAsk);
        rememberTurnContext(agentPayload);

        const {result, readyToConfirm} = buildAgentTurnResult(agentPayload);
        if(readyToConfirm && agentPayload.pendingProposal) {
            // The summary asks for confirmation — this turn only holds the proposal, and the
            // chat offers the action. Creating here would answer the question on the
            // producer's behalf. The owner is stamped now: it is the one that was agreed on,
            // and the confirmation may come after the producer has navigated elsewhere.
            holdProposal(agentPayload.pendingProposal, {scope, eid, pid});
        }
        return result;
    };
}
